import React, { useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Select, Skeleton, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import Flex from '../../../components/Flex';
import useScrapingUsage from '../../../hooks/useScrapingUsage';
import { PiPlugsLight } from "react-icons/pi";
import { IconContext } from "react-icons";
import useScrapingCredentials from "../../../hooks/useScrapingCredentials";
import useMe from "../../../hooks/useMe";
import Swal from "sweetalert2";

export function PjnNumberInput({ getFieldDecorator, isFieldTouched, getFieldError, expedient, editing, setFieldsValue }) {

  const pjnInputError = isFieldTouched("pjn") && getFieldError("pjn");
  const { usage, loading: usageLoading } = useScrapingUsage('pjn');
  const { credentials, loading: pjnLoading } = useScrapingCredentials("pjn");
  const emptyCredentials = !credentials || credentials.length === 0;
  const { user } = useMe()

  const noUsageLeft = !(usage && usage.used < usage.total)
  const alreadyVinculted = editing && expedient && expedient.pjn && expedient.pjn.length > 0;

  if (usageLoading || pjnLoading) {
    return (
      <Form.Item>
        <Skeleton className="responsive-input" paragraph={{ rows: 1 }} active />
      </Form.Item>
    );
  }

  return (
    <Flex flexDirection="column">
      <Form.Item
        help={
          pjnInputError ? "Ingrese solo números sin prefijo ni sufijo" :
            emptyCredentials ? "No posee credenciales PJN" :
              noUsageLeft ? "Limite PJN de su plan alcanzado" :
                `Vinculaciones PJN restantes: ${usage ? usage.left : "..."}`
        }
        labelCol={{ span: 24 }}
        key="pjn"
        label={
          <Flex flexDirection="row" gap="1ch">
            <Typography.Text>
              Causa PJN{" "}
              <Tooltip title="Solo numero de PJN">
                <InfoCircleOutlined />
              </Tooltip>
            </Typography.Text>
            <PrivateOrPublicToggle
              getFieldDecorator={getFieldDecorator}
              editing={editing}
              expedient={expedient}
            />
          </Flex>
        }
        validateStatus={pjnInputError ? "error" : "success"}
      >
        {getFieldDecorator("pjn", {
          initialValue: editing ? expedient.pjn || null : null
        })(
          <PjnNumberInputMasked
            className="responsive-input"
            disabled={emptyCredentials || (noUsageLeft && !alreadyVinculted)}
          />
        )}
      </Form.Item>

      {emptyCredentials ?
        <a href="/settings#integraciones" style={{ alignSelf: "end" }}>
          <Button type="primary">Configurar PJN</Button>
        </a> :
        noUsageLeft ?
          <a href="https://metajuridico.com/planes_actualizacion.html" target="_blank" style={{ alignSelf: "end" }}>
            <Button type="primary">Ampliar Plan</Button>
          </a> : null}

    </Flex>
  );
}

export function PjnNumberInputMasked({ value, onChange, disabled }) {
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(
    pjnJurisdictions[0].value
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [number, setNumber] = useState("");

  useEffect(() => {
    if (value) {
      try {
        const [jurisdiction, full] = value.split(" ");
        const [parsedNumber, parsedYear] = full.split("/");
        setSelectedJurisdiction(jurisdiction);
        setYear(parsedYear);
        setNumber(parsedNumber.replace(/^0+/, ""));
      } catch (error) { }
    }
  }, [value]);

  useEffect(() => {
    if (selectedJurisdiction && year && number) {
      onChange &&
        onChange(`${selectedJurisdiction} ${number.padStart(6, "0")}/${year}`);
    }
  }, [selectedJurisdiction, year, number]);

  const clearPjn = () => {
    setSelectedJurisdiction(pjnJurisdictions[0].value);
    setYear(new Date().getFullYear());
    setNumber("");
    onChange && onChange("");
  };

  return (
    <Flex flexDirection="row" gap="1ch" alignItems="center" flexGrow={1}>
      <JurisdictionSelect
        onChange={setSelectedJurisdiction}
        value={selectedJurisdiction}
        disabled={disabled}
      />
      <Flex flexDirection="column" gap="0.5ch" flexGrow={1}>
        <label style={{ fontSize: "10px" }}>Numero</label>
        <Input
          disabled={disabled}
          style={{ width: "100%" }}
          value={number}
          onChange={e => setNumber(e.target.value)}
          placeholder="123456"
          maxLength={6}
          className="responsive-input"
        />
      </Flex>
      <YearInput value={year} handleChange={setYear} disabled={disabled} />
      <Flex flexDirection="column" gap="0.5ch" alignItems="center">
        <label style={{ fontSize: "10px" }}>&nbsp;</label>
        <ClearScrapingButton onClick={clearPjn} disabled={disabled} />
      </Flex>
    </Flex>
  );
}

const PrivateOrPublicToggle = ({ getFieldDecorator, editing, expedient }) => (
  <Form.Item style={{ marginBottom: 0 }}>
    {getFieldDecorator("is_pjn_private", {
      initialValue: editing && expedient ? expedient.is_pjn_private : false,
      valuePropName: "checked",
      trigger: "onChange",
    })(<ToggleButton />)}
  </Form.Item>
);

const ToggleButton = ({ checked = false, onChange }) => (
  <Tooltip title={checked ? "Consulta privada" : "Consulta pública"}>
    <Button
      style={{
        height: "32px",
        width: "32px",
        padding: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "50%",
      }}
      type={checked ? "primary" : "default"}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
    >
      {checked ? (
        <LockOutlined style={{ fontSize: "18px", pointerEvents: "none" }} />
      ) : (
        <UnlockOutlined style={{ fontSize: "18px", pointerEvents: "none" }} />
      )}
    </Button>
  </Tooltip>
);

export const JurisdictionSelect = ({ value, onChange, disabled }) => (
  <Flex flexDirection="column" gap="0.5ch">
    <label htmlFor="jurisdiction" style={{ fontSize: "10px" }}>
      Jurisdicción
    </label>
    <Select
      optionLabelProp="label"
      dropdownMatchSelectWidth={false}
      disabled={disabled}
      onChange={onChange}
      value={value}
      className="responsive-input"
    >
      {pjnJurisdictions.map(pjn => (
        <Select.Option key={pjn.value} value={pjn.value}>
          {pjn.label} - {pjn.label2}
        </Select.Option>
      ))}
    </Select>
  </Flex>
);

export const YearInput = ({ value, handleChange, disabled }) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1999 },
    (_, i) => currentYear - i
  );

  return (
    <Flex flexDirection="column" gap="0.5ch">
      <label style={{ fontSize: "10px" }}>Año</label>
      <Select
        disabled={disabled}
        value={value}
        onChange={handleChange}
        placeholder={currentYear}
        className="responsive-input"
      >
        {years.map(year => (
          <Select.Option key={year} value={year}>
            {year}
          </Select.Option>
        ))}
      </Select>
    </Flex>
  );
};

export const ClearScrapingButton = ({ onClick, disabled, tooltipText = "Eliminar causa PJN" }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ height: "43px" }}>
      <Tooltip title={tooltipText}>
        <Button
          disabled={disabled}
          style={{
            height: "43px",
            width: "40px",
            display: "flex",
            justifyContent: "center",
            borderRadius: "13px",
            alignItems: "center"
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={onClick}
          icon={
            <IconContext.Provider
              value={{
                size: "22px",
                color: hovered ? "#FF5879" : "#95001D",
                cursor: "pointer"
              }}
            >
              <PiPlugsLight />
            </IconContext.Provider>
          }
        />
      </Tooltip>
    </div>
  );
};

const pjnJurisdictions = [
  {
    label: "CSJ",
    label2: "Corte Suprema de Justicia de la Nación",
    value: "CSJ"
  },
  {
    label: "CIV",
    label2: "Cámara Nacional de Apelaciones en lo Civil",
    value: "CIV"
  },
  {
    label: "CAF",
    label2:
      "Cámara Nacional de Apelaciones en lo Contencioso Administrativo Federal",
    value: "CAF"
  },
  {
    label: "CCF",
    label2: "Cámara Nacional de Apelaciones en lo Civil y Comercial Federal",
    value: "CCF"
  },
  { label: "CNE", label2: "Cámara Nacional Electoral", value: "CNE" },
  {
    label: "CSS",
    label2: "Camara Federal de la Seguridad Social",
    value: "CSS"
  },
  {
    label: "CPE",
    label2: "Cámara Nacional de Apelaciones en lo Penal Económico",
    value: "CPE"
  },
  {
    label: "CNT",
    label2: "Cámara Nacional de Apelaciones del Trabajo",
    value: "CNT"
  },
  {
    label: "CFP",
    label2: "Camara Criminal y Correccional Federal",
    value: "CFP"
  },
  {
    label: "CCC",
    label2: "Camara Nacional de Apelaciones en lo Criminal y Correccional",
    value: "CCC"
  },
  {
    label: "COM",
    label2: "Camara Nacional de Apelaciones en lo Comercial",
    value: "COM"
  },
  { label: "CPF", label2: "Camara Federal de Casación Penal", value: "CPF" },
  { label: "CPN", label2: "Camara Nacional Casacion Penal", value: "CPN" },
  { label: "FBB", label2: "Justicia Federal de Bahia Blanca", value: "FBB" },
  {
    label: "FCR",
    label2: "Justicia Federal de Comodoro Rivadavia",
    value: "FCR"
  },
  { label: "FCB", label2: "Justicia Federal de Córdoba", value: "FCB" },
  { label: "FCT", label2: "Justicia Federal de Corrientes", value: "FCT" },
  { label: "FGR", label2: "Justicia Federal de General Roca", value: "FGR" },
  { label: "FLP", label2: "Justicia Federal de La Plata", value: "FLP" },
  { label: "FMP", label2: "Justicia Federal de Mar del Plata", value: "FMP" },
  { label: "FMZ", label2: "Justicia Federal de Mendoza", value: "FMZ" },
  { label: "FPO", label2: "Justicia Federal de Posadas", value: "FPO" },
  { label: "FPA", label2: "Justicia Federal de Paraná", value: "FPA" },
  { label: "FRE", label2: "Justicia Federal de Resistencia", value: "FRE" },
  { label: "FSA", label2: "Justicia Federal de Salta", value: "FSA" },
  { label: "FRO", label2: "Justicia Federal de Rosario", value: "FRO" },
  { label: "FSM", label2: "Justicia Federal de San Martin", value: "FSM" },
  { label: "FTU", label2: "Justicia Federal de Tucuman", value: "FTU" }
];
